import React, { useMemo, useState } from 'react';
import { Collapse, Input, Space, Table, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  APPLICATIONS,
  ROLE_USER_TYPES,
  applicationLabel,
  getAccessModule,
  getAccessModules,
  groupByApplication,
  modulesForPermission,
  permissionMeta,
} from '../../assets/utils/accessCatalog';
import { canAccess, isPlatformPermission, isPlatformRole } from '../../assets/utils/permissions';
import AffectsCell from './AffectsCell';

const { Text } = Typography;

function roleTypeLabel(userType) {
  return ROLE_USER_TYPES.find((t) => t.key === userType)?.label || userType || 'Other';
}

function permissionName(record) {
  return String(record?.name || '');
}

export default function RbacGlossaryTab({ permissions = [], roles = [] }) {
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();

  const permRows = useMemo(() => {
    return permissions
      .filter((p) => !isPlatformPermission(permissionName(p)))
      .map((p) => permissionMeta(permissionName(p), p))
      .filter((row) => {
        if (!query) return true;
        return [row.name, row.displayName, row.description, row.affects, row.resource]
          .join(' ')
          .toLowerCase()
          .includes(query);
      });
  }, [permissions, query]);

  const permGroups = useMemo(
    () => groupByApplication(permRows, (row) => row.application),
    [permRows]
  );

  const rolesByType = useMemo(() => {
    const visible = roles.filter((r) => !isPlatformRole(r?.name));
    const groups = ROLE_USER_TYPES.map((t) => ({ ...t, items: [] }));
    const other = { key: 'other', label: 'Other', items: [] };
    visible.forEach((role) => {
      const type = String(role.user_type || role.userType || '').toLowerCase();
      const bucket = groups.find((g) => g.key === type);
      const names = (role.permissions || []).map((p) => (typeof p === 'string' ? p : p?.name)).filter(Boolean);
      const previewMap = {};
      names.forEach((n) => { previewMap[n] = true; });
      const unlocked = getAccessModules().filter((mod) => canAccess(mod.key, previewMap));
      const row = {
        ...role,
        permissionCount: names.length || Number(role.permission_count) || 0,
        unlocks: unlocked.map((m) => m.label).join(', ') || '—',
        landing: role.home_module
          ? `${getAccessModule(role.home_module)?.label || role.home_module}${Number(role.home_priority) ? ` (priority ${role.home_priority})` : ''}`
          : 'Default order',
      };
      if (bucket) bucket.items.push(row);
      else other.items.push(row);
    });
    const filled = groups.filter((g) => g.items.length);
    return other.items.length ? [...filled, other] : filled;
  }, [roles]);

  const filteredRoleGroups = useMemo(() => {
    if (!query) return rolesByType;
    return rolesByType
      .map((g) => ({
        ...g,
        items: g.items.filter((r) => {
          const blob = [
            r.name,
            r.display_name,
            r.description,
            r.unlocks,
            r.landing,
            r.user_type,
          ].join(' ').toLowerCase();
          return blob.includes(query);
        }),
      }))
      .filter((g) => g.items.length);
  }, [rolesByType, query]);

  const permColumns = [
    {
      title: 'Permission',
      dataIndex: 'name',
      width: 260,
      render: (name, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.displayName}</Text>
          <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Application',
      dataIndex: 'application',
      width: 130,
      render: (v) => <Tag>{applicationLabel(v)}</Tag>,
    },
    {
      title: 'What it does',
      dataIndex: 'description',
      width: 320,
      render: (v) => <Text>{v || '—'}</Text>,
    },
    {
      title: 'Affects',
      dataIndex: 'name',
      key: 'affects',
      render: (name) => <AffectsCell modules={modulesForPermission(name)} />,
    },
  ];

  const roleColumns = [
    {
      title: 'Role',
      dataIndex: 'display_name',
      render: (v, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v || row.name}</Text>
          <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.name}</Text>
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (v) => v || '—',
    },
    {
      title: 'Permissions',
      dataIndex: 'permissionCount',
      width: 110,
    },
    {
      title: 'Landing',
      dataIndex: 'landing',
      width: 180,
    },
    {
      title: 'Unlocks in Care Admin',
      dataIndex: 'unlocks',
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Search permissions or roles…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Permissions by application</Text>
        <Collapse
          defaultActiveKey={APPLICATIONS.map((a) => a.key)}
          items={permGroups.map((g) => ({
            key: g.key,
            label: `${g.label} (${g.items.length})`,
            children: (
              <Table
                size="small"
                rowKey="name"
                pagination={false}
                columns={permColumns}
                dataSource={g.items}
                scroll={{ x: 900 }}
              />
            ),
          }))}
        />
      </div>

      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Roles by user type</Text>
        <Collapse
          defaultActiveKey={filteredRoleGroups.map((g) => g.key)}
          items={filteredRoleGroups.map((g) => ({
            key: g.key,
            label: `${g.label} (${g.items.length})`,
            children: (
              <Table
                size="small"
                rowKey={(r) => r.id || r.role_id || r.name}
                pagination={false}
                columns={roleColumns}
                dataSource={g.items}
              />
            ),
          }))}
        />
      </div>
    </Space>
  );
}
